import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Bookmark, Trash2 } from 'lucide-react';
import { useFilterPresets } from '@/hooks/use-filter-presets';

export interface FilterPresetsMenuProps {
  currentParams: URLSearchParams;
  onApply: (params: URLSearchParams) => void;
}

export function FilterPresetsMenu({ currentParams, onApply }: FilterPresetsMenuProps) {
  const { presets, savePreset, deletePreset, applyPreset } = useFilterPresets();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const handleSave = () => {
    if (newName.trim()) {
      savePreset(newName.trim(), currentParams);
      setNewName('');
      setSaveDialogOpen(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={(p) => (
            <Button {...p} variant="outline" size="sm">
              <Bookmark className="mr-2 h-4 w-4" />
              Presets
            </Button>
          )}
        />
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem onClick={() => setSaveDialogOpen(true)}>
            Save current filters…
          </DropdownMenuItem>
          {presets.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuLabel>Saved presets</DropdownMenuLabel>
                {presets.map((preset) => (
                  <div key={preset.id} className="flex items-center justify-between pr-1">
                    <DropdownMenuItem
                      className="flex-1"
                      onClick={() => {
                        const p = applyPreset(preset.id);
                        if (p) onApply(p);
                      }}
                    >
                      {preset.name}
                    </DropdownMenuItem>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deletePreset(preset.id);
                      }}
                      className="rounded p-1 text-muted-foreground hover:bg-accent"
                      aria-label={`Delete ${preset.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </DropdownMenuGroup>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save filter preset</DialogTitle>
            <DialogDescription>
              Give this filter combination a name so you can recall it later. Presets are stored
              locally in this browser only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="preset-name">Preset name</Label>
            <Input
              id="preset-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Unused anxiety seeds"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!newName.trim()}>
              Save preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
